import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  BellIcon, 
  FunnelIcon, 
  DocumentArrowDownIcon, 
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { announcementsAPI } from '../services/api';
import { toast } from 'react-toastify';
import NotificationBell from '../components/NotificationBell';
import { useAuth } from '../context/AuthContext';

function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser, isAdmin, isManager } = useAuth();

  // Determine which dashboard to go back to
  const getBackDashboard = () => {
    // First, check if fromDashboard was passed in location state
    if (location.state?.fromDashboard) {
      return location.state.fromDashboard;
    }
    
    // Otherwise, determine based on user role and referrer
    const referrer = document.referrer;
    
    // Check referrer to see which dashboard they came from
    if (referrer.includes('/user-dashboard')) {
      return '/user-dashboard';
    } else if (referrer.includes('/manager-dashboard')) {
      return '/manager-dashboard';
    } else if (referrer.includes('/') && !referrer.includes('/user-dashboard') && !referrer.includes('/manager-dashboard')) {
      return '/'; // Admin dashboard
    }
    
    // Fallback: determine based on user role
    if (isManager() && !isAdmin()) {
      return '/manager-dashboard';
    } else if (isAdmin()) {
      return '/';
    } else {
      return '/user-dashboard';
    }
  };

  const handleBackToDashboard = () => {
    const dashboardPath = getBackDashboard();
    navigate(dashboardPath);
  };

  // Load user data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Fetch announcements
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await announcementsAPI.getAnnouncements({
        includeExpired: 'true',
        unreadOnly: filter === 'unread' ? 'true' : 'false'
      });
      let fetchedAnnouncements = response.data.announcements || [];
      
      // Client-side filter for read status if needed
      if (filter === 'read') {
        fetchedAnnouncements = fetchedAnnouncements.filter(a => a.isRead);
      }
      
      setAnnouncements(fetchedAnnouncements);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [filter]);

  // Handle URL parameter for opening specific announcement
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const announcementId = params.get('id');
    
    if (announcementId && announcements.length > 0) {
      const announcement = announcements.find(a => a.id === announcementId);
      if (announcement) {
        handleAnnouncementClick(announcement);
      }
    }
  }, [location.search, announcements]);

  const handleAnnouncementClick = async (announcement) => {
    setSelectedAnnouncement(announcement);
    
    // Mark as read if not already read
    if (!announcement.isRead) {
      try {
        await announcementsAPI.markAsRead(announcement.id);
        // Update local state
        setAnnouncements(prev => 
          prev.map(a => 
            a.id === announcement.id 
              ? { ...a, isRead: true, readAt: new Date().toISOString() }
              : a
          )
        );
        // Update selected announcement
        setSelectedAnnouncement(prev => ({
          ...prev,
          isRead: true,
          readAt: new Date().toISOString()
        }));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadAnnouncements = announcements.filter(a => !a.isRead);
      await Promise.all(
        unreadAnnouncements.map(a => announcementsAPI.markAsRead(a.id))
      );
      toast.success('All announcements marked as read');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-200',
      normal: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isImageFile = (filename) => {
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
    return imageExtensions.test(filename);
  };

  const activeAnnouncements = announcements.filter(a => !isExpired(a.expiresAt));
  const expiredAnnouncements = announcements.filter(a => isExpired(a.expiresAt));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-6">
      {/* Header - Professional Design with Animations (Same as UserDashboard) */}
      <div className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 shadow-professional-lg animate-fade-in">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-5 sm:py-7 gap-4">
            <div className="flex items-center w-full sm:w-auto animate-slide-in-right">
              <div className="flex-shrink-0 relative">
                <div className="h-14 w-14 sm:h-18 sm:w-18 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-professional-lg hover-lift transition-smooth ring-2 ring-white/20">
                  <BellIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-green-500 rounded-full border-2 border-white shadow-professional animate-pulse"></div>
              </div>
              <div className="ml-4 sm:ml-6 flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-white drop-shadow-lg tracking-tight">
                  Announcements
                </h1>
                <p className="text-sm sm:text-base text-slate-200 mt-1.5 font-medium flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Stay updated with important announcements
                </p>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-3 animate-fade-in stagger-1">
              <button
                onClick={handleBackToDashboard}
                className="group inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 border border-white/20 text-sm font-semibold rounded-xl text-white bg-white/5 backdrop-blur-sm hover:bg-white/15 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-smooth shadow-professional hover-lift"
                title="Back to Dashboard"
              >
                <HomeIcon className="h-5 w-5 sm:mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
              {/* <NotificationBell /> */}
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 sm:px-6 py-2.5 sm:py-3 border border-white/20 text-sm font-semibold rounded-xl text-white bg-white/5 backdrop-blur-sm hover:bg-red-600/80 hover:border-red-500/50 focus:outline-none focus:ring-2 focus:ring-red-400/50 transition-smooth shadow-professional hover-lift self-end sm:self-auto"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Breadcrumb / Back Button */}
        {/* <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-indigo-600 font-medium transition-colors duration-200 group"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            Back to Dashboard
          </button>
        </div> */}

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'unread'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Unread
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  filter === 'read'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Read
              </button>
            </div>
          </div>
          
          {announcements.some(a => !a.isRead) && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Announcements List */}
            <div className="lg:col-span-1 space-y-4">
              {activeAnnouncements.length === 0 && expiredAnnouncements.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-gray-500">No announcements to display</p>
                </div>
              ) : (
                <>
                  {/* Active Announcements */}
                  {activeAnnouncements.length > 0 && (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-3">Active</h2>
                      <div className="space-y-2">
                        {activeAnnouncements.map((announcement) => (
                          <button
                            key={announcement.id}
                            onClick={() => handleAnnouncementClick(announcement)}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                              selectedAnnouncement?.id === announcement.id
                                ? 'border-indigo-600 bg-indigo-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            } ${!announcement.isRead ? 'bg-blue-50' : ''}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${
                                  !announcement.isRead ? 'text-gray-900' : 'text-gray-700'
                                }`}>
                                  {announcement.title}
                                </p>
                                <div className="flex items-center mt-2 space-x-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getPriorityBadge(announcement.priority)}`}>
                                    {announcement.priority}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(announcement.createdAt)}
                                  </span>
                                </div>
                              </div>
                              {!announcement.isRead && (
                                <div className="ml-2 flex-shrink-0">
                                  <div className="h-2.5 w-2.5 bg-blue-600 rounded-full"></div>
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Expired Announcements */}
                  {expiredAnnouncements.length > 0 && (
                    <div className="mt-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-3">Expired</h2>
                      <div className="space-y-2">
                        {expiredAnnouncements.map((announcement) => (
                          <button
                            key={announcement.id}
                            onClick={() => handleAnnouncementClick(announcement)}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all opacity-60 ${
                              selectedAnnouncement?.id === announcement.id
                                ? 'border-indigo-600 bg-indigo-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700">
                                  {announcement.title}
                                </p>
                                <div className="flex items-center mt-2 space-x-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-gray-100 text-gray-600 border-gray-200">
                                    Expired
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(announcement.expiresAt)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Announcement Detail */}
            <div className="lg:col-span-2">
              {selectedAnnouncement ? (
                <div className="bg-white rounded-lg shadow-lg">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900">
                          {selectedAnnouncement.title}
                        </h2>
                        <div className="flex items-center mt-3 space-x-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPriorityBadge(selectedAnnouncement.priority)}`}>
                            {selectedAnnouncement.priority.toUpperCase()} PRIORITY
                          </span>
                          {isExpired(selectedAnnouncement.expiresAt) && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-gray-100 text-gray-600 border-gray-200">
                              EXPIRED
                            </span>
                          )}
                          {selectedAnnouncement.isRead && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-green-100 text-green-800 border-green-200">
                              READ
                            </span>
                          )}
                        </div>
                        <div className="mt-3 text-sm text-gray-600">
                          <p>Posted by: {selectedAnnouncement.creator ? `${selectedAnnouncement.creator.firstName} ${selectedAnnouncement.creator.lastName}` : 'Admin'}</p>
                          <p>Date: {formatDate(selectedAnnouncement.createdAt)}</p>
                          {selectedAnnouncement.expiresAt && (
                            <p>Expires: {formatDate(selectedAnnouncement.expiresAt)}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAnnouncement(null)}
                        className="ml-4 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    {/* Content */}
                    <div 
                      className="prose max-w-none text-gray-800"
                      dangerouslySetInnerHTML={{ __html: selectedAnnouncement.content }}
                    />
                    
                    {/* Attachments */}
                    {selectedAnnouncement.attachments && selectedAnnouncement.attachments.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                          <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                          Attachments ({selectedAnnouncement.attachments.length})
                        </h3>
                        <div className="space-y-3">
                          {selectedAnnouncement.attachments.map((attachment, index) => {
                            const isImage = isImageFile(attachment.name || attachment.url);
                            
                            return isImage ? (
                              // Image Preview
                              <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                <div className="p-3 bg-gray-100 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {attachment.name}
                                    </p>
                                    <a
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium ml-2"
                                    >
                                      Open
                                    </a>
                                  </div>
                                  {attachment.size && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {(attachment.size / 1024).toFixed(2)} KB
                                    </p>
                                  )}
                                </div>
                                <div className="p-4 bg-white flex justify-center">
                                  <img 
                                    src={attachment.url} 
                                    alt={attachment.name}
                                    className="max-w-full max-h-96 rounded-lg shadow-md object-contain"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f3f4f6" width="200" height="200"/><text x="50%" y="50%" font-size="14" fill="%239ca3af" text-anchor="middle" dominant-baseline="middle">Image not available</text></svg>';
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              // Document Download Link
                              <a
                                key={index}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border-2 border-gray-200 hover:border-indigo-300"
                              >
                                <DocumentArrowDownIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {attachment.name}
                                  </p>
                                  {attachment.size && (
                                    <p className="text-xs text-gray-500">
                                      {(attachment.size / 1024).toFixed(2)} KB
                                    </p>
                                  )}
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                  <BellIcon className="mx-auto h-16 w-16 text-gray-400" />
                  <p className="mt-4 text-gray-500">Select an announcement to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Announcements;

